import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { GraduationCap, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DISCIPLINES = ['PT', 'PTA', 'OT', 'COTA', 'SLP', 'Other / Unsure'];

export default function PublicProspectForm() {
  const [schools, setSchools] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    discipline: '', school_id: '', school_name_other: '',
    graduating_year: '', graduating_term: '',
    city: '', state: '',
    notes: '', referred_by: '',
  });

  useEffect(() => {
    axios.get(`${API}/public/prospects/schools`).then(res => setSchools(res.data || [])).catch(() => {});
  }, []);

  const submit = async () => {
    if (!form.first_name.trim() || !form.last_name.trim() || !form.email.trim()) {
      toast.error('Please fill in your name and email');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      if (form.school_id === '__other__') {
        delete payload.school_id;
        payload.school_name = form.school_name_other;
      } else if (form.school_id) {
        const s = schools.find(x => x.id === form.school_id);
        payload.school_name = s?.name;
      }
      delete payload.school_name_other;
      if (payload.graduating_year) payload.graduating_year = parseInt(payload.graduating_year);
      else delete payload.graduating_year;
      Object.keys(payload).forEach(k => { if (payload[k] === '' || payload[k] === undefined) delete payload[k]; });
      await axios.post(`${API}/public/prospects/submit`, payload);
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submission failed — please try again');
    } finally { setSaving(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4">
        <div className="max-w-xl mx-auto">
          <Card className="p-8 text-center">
            <CheckCircle2 className="mx-auto text-emerald-500 mb-4" size={56} />
            <h1 className="text-2xl font-bold mb-2">Thanks for reaching out!</h1>
            <p className="text-muted-foreground">We've received your information and a member of our recruiting team will be in touch soon. In the meantime, feel free to learn more about what we do at Elite Home Rehab.</p>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-6">
          <GraduationCap size={48} className="mx-auto text-primary mb-2" />
          <h1 className="text-3xl font-bold">Stay in Touch with Elite Home Rehab</h1>
          <p className="text-muted-foreground mt-2">Tell us about yourself and we'll keep you posted on opportunities as you near graduation.</p>
        </div>

        <Card className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>First Name *</Label><Input value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid="public-first-name" /></div>
            <div><Label>Last Name *</Label><Input value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid="public-last-name" /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} data-testid="public-email" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Discipline</Label>
              <select value={form.discipline} onChange={e => setForm({ ...form, discipline: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="public-discipline">
                <option value="">Select…</option>
                {DISCIPLINES.map(d => <option key={d}>{d}</option>)}
              </select>
            </div>
            <div>
              <Label>School</Label>
              <select value={form.school_id} onChange={e => setForm({ ...form, school_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="public-school">
                <option value="">Select your school…</option>
                {schools.map(s => <option key={s.id} value={s.id}>{s.name}{s.city ? ` (${s.city}${s.state ? ', ' + s.state : ''})` : ''}</option>)}
                <option value="__other__">Not listed — type below</option>
              </select>
              {form.school_id === '__other__' && (
                <Input className="mt-2" placeholder="Type your school name" value={form.school_name_other} onChange={e => setForm({ ...form, school_name_other: e.target.value })} />
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Graduating Year</Label>
              <Input type="number" placeholder="2026" value={form.graduating_year} onChange={e => setForm({ ...form, graduating_year: e.target.value })} data-testid="public-grad-year" />
            </div>
            <div>
              <Label>Graduating Term</Label>
              <select value={form.graduating_term} onChange={e => setForm({ ...form, graduating_term: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
                <option value="">—</option>
                <option>Spring</option><option>Summer</option><option>Fall</option><option>Winter</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div><Label>City</Label><Input value={form.city} onChange={e => setForm({ ...form, city: e.target.value })} /></div>
            <div><Label>State</Label><Input value={form.state} onChange={e => setForm({ ...form, state: e.target.value })} maxLength={2} /></div>
          </div>
          <div><Label>Referred by</Label><Input placeholder="Optional — name of person who referred you" value={form.referred_by} onChange={e => setForm({ ...form, referred_by: e.target.value })} /></div>
          <div><Label>Anything else we should know?</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={3} /></div>

          <Button onClick={submit} disabled={saving} className="w-full" data-testid="public-submit-btn">
            {saving ? 'Sending…' : 'Submit'}
          </Button>
          <p className="text-xs text-center text-muted-foreground">Your info goes directly to our recruiting team. We won't share it.</p>
        </Card>
      </div>
    </div>
  );
}
